import React from 'react';
import { Box, Card, CardContent, TextField, Button, Typography, IconButton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { loginUser } from '../store/slices/authSlice';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { LightMode, DarkMode, Spa } from '@mui/icons-material';
import { useThemeMode } from '../context/ThemeContext';

const validationSchema = yup.object({
  email: yup.string('Enter email').email('Invalid email').required('Email is required'),
  password: yup.string('Enter password').required('Password is required')
});

export default function LoginPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { loading, error } = useSelector(state => state.auth);
  const { isDark, toggleTheme, colors } = useThemeMode();

  const formik = useFormik({
    initialValues: {
      email: '',
      password: ''
    },
    validationSchema: validationSchema,
    onSubmit: async (values) => {
      const result = await dispatch(loginUser(values));
      if (result.payload?.token) {
        navigate('/dashboard');
      }
    }
  });

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '100vh',
        background: isDark 
          ? 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)'
          : 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 50%, #f0f9ff 100%)',
        position: 'relative'
      }}
    >
      {/* Theme Toggle */}
      <IconButton 
        onClick={toggleTheme}
        sx={{ 
          position: 'absolute', 
          top: 24, 
          right: 24,
          bgcolor: isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
          '&:hover': { bgcolor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.1)' }
        }}
      >
        {isDark ? <LightMode sx={{ color: '#f59e0b' }} /> : <DarkMode sx={{ color: '#64748b' }} />}
      </IconButton>

      {/* Decorative Elements */}
      <Box sx={{ 
        position: 'absolute', 
        top: -100, 
        right: -100, 
        width: 300, 
        height: 300, 
        background: `radial-gradient(circle, ${colors.primary}20 0%, transparent 70%)`,
        borderRadius: '50%'
      }} />
      <Box sx={{ 
        position: 'absolute', 
        bottom: -50, 
        left: -50, 
        width: 200, 
        height: 200, 
        background: `radial-gradient(circle, ${colors.primary}15 0%, transparent 70%)`,
        borderRadius: '50%'
      }} />

      <Card sx={{ 
        width: '100%', 
        maxWidth: 420, 
        boxShadow: isDark ? '0 25px 50px rgba(0,0,0,0.5)' : '0 25px 50px rgba(29,202,255,0.15)',
        borderRadius: 4,
        bgcolor: isDark ? '#1e293b' : '#ffffff',
        border: isDark ? '1px solid rgba(255,255,255,0.08)' : 'none',
        position: 'relative',
        overflow: 'visible'
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box
              component="img"
              src="/caldea-logo.png"
              alt="Caldea"
              sx={{
                height: 48,
                objectFit: 'contain',
                margin: '0 auto 16px',
                display: 'block',
                filter: isDark ? 'brightness(1.2)' : 'none'
              }}
              onError={(e) => { e.target.style.display = 'none'; }}
            />
            <Typography variant="h5" sx={{ fontWeight: 700, color: isDark ? '#f1f5f9' : '#0f172a', mb: 0.5 }}>
              Caldea Club
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', color: colors.primary, fontWeight: 600, fontSize: '0.75rem' }}>
              Powered by PEpL
            </Typography>
            <Typography variant="body2" sx={{ color: isDark ? '#94a3b8' : '#64748b', mt: 1 }}>
              Panel de Administración
            </Typography>
          </Box>

          {error && (
            <Box sx={{ 
              color: colors.error, 
              mb: 2, 
              p: 1.5, 
              backgroundColor: isDark ? 'rgba(239,68,68,0.15)' : '#fef2f2', 
              borderRadius: 2,
              fontSize: '0.85rem'
            }}>
              {error}
            </Box>
          )}

          <form onSubmit={formik.handleSubmit}>
            <TextField
              fullWidth
              label="Email"
              name="email"
              type="email"
              value={formik.values.email}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              margin="normal"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'
                }
              }}
            />
            <TextField
              fullWidth
              label="Contraseña"
              name="password"
              type="password"
              value={formik.values.password}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.password && Boolean(formik.errors.password)}
              helperText={formik.touched.password && formik.errors.password}
              margin="normal"
              variant="outlined"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: isDark ? 'rgba(255,255,255,0.05)' : '#f8fafc'
                }
              }}
            />
            <Button
              fullWidth
              variant="contained"
              type="submit"
              sx={{ 
                mt: 3, 
                py: 1.5,
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primaryDark} 100%)`,
                boxShadow: '0 4px 14px rgba(29,202,255,0.35)',
                borderRadius: 2,
                fontWeight: 600,
                fontSize: '0.95rem',
                '&:hover': {
                  background: `linear-gradient(135deg, ${colors.primaryLight} 0%, ${colors.primary} 100%)`,
                }
              }}
              disabled={loading}
            >
              {loading ? 'Iniciando sesión...' : 'Iniciar Sesión'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </Box>
  );
}
